import { useEffect } from 'react';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { AppButton, AppTextField } from '../../design-system';
import { FormProvider, useForm } from '../../forms';
import {
  CreateWaferImagingTemplateRequestSchema,
  type CreateWaferImagingTemplateRequest,
  type WaferImagingTemplateListItem,
} from '@cnapse/shared/schemas.js';
import * as dt from '@cnapse/shared/data.types.js';
import { LightingModesEditor } from './LightingModesEditor';

const WAFER_SIZE_OPTIONS = Object.values(dt.WAFER_SIZE);
const ID_SIDE_OPTIONS = ['top', 'bottom'] as const;

export interface WaferImagingTemplateFormDialogProps {
  open: boolean;
  template: WaferImagingTemplateListItem | null;
  onClose: () => void;
  onSubmit: (data: CreateWaferImagingTemplateRequest) => Promise<void>;
  submitting?: boolean;
  canEdit: boolean;
}

const DEFAULT_MODES: CreateWaferImagingTemplateRequest['startingLightingModes'] = [
  { mode: 1, startPower: 2 },
  { mode: 3, startPower: 2 },
  { mode: 5, startPower: 2 },
];

export function WaferImagingTemplateFormDialog({
  open,
  template,
  onClose,
  onSubmit,
  submitting = false,
  canEdit,
}: WaferImagingTemplateFormDialogProps) {
  const isEdit = template != null;
  const lockGeometry = isEdit && template.isSystemTemplate;

  const methods = useForm<CreateWaferImagingTemplateRequest>({
    resolver: zodResolver(CreateWaferImagingTemplateRequestSchema) as Resolver<CreateWaferImagingTemplateRequest>,
    defaultValues: {
      name: '',
      waferSize: dt.WAFER_SIZE.Size300mm,
      idSide: 'bottom',
      markOffsetDeg: 0,
      startingLightingModes: DEFAULT_MODES,
    },
  });

  useEffect(() => {
    if (!open) return;
    if (template) {
      methods.reset({
        name: template.name,
        waferSize: template.waferSize as dt.WAFER_SIZE,
        idSide: template.idSide as 'top' | 'bottom',
        markOffsetDeg: template.markOffsetDeg,
        startingLightingModes: template.startingLightingModes,
      });
    } else {
      methods.reset({
        name: '',
        waferSize: dt.WAFER_SIZE.Size300mm,
        idSide: 'bottom',
        markOffsetDeg: 0,
        startingLightingModes: DEFAULT_MODES,
      });
    }
  }, [open, template, methods]);

  const handleSubmit = async (data: CreateWaferImagingTemplateRequest) => {
    await onSubmit(data);
  };

  const modes = methods.watch('startingLightingModes');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit scan template' : 'New scan template'}</DialogTitle>
      <DialogContent>
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit}
          variant="plain"
          loading={submitting}
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isEdit && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Wafers with learned lighting are not updated automatically. Clear learned lighting
                on affected wafers if captures fail after changing modes.
              </Alert>
            )}
            <AppTextField
              label="Name"
              fullWidth
              size="small"
              disabled={!canEdit || submitting}
              {...methods.register('name')}
              error={Boolean(methods.formState.errors.name)}
              helperText={methods.formState.errors.name?.message}
            />
            <FormControl fullWidth size="small" disabled={!canEdit || submitting || lockGeometry}>
              <InputLabel>Wafer size</InputLabel>
              <Select
                label="Wafer size"
                value={methods.watch('waferSize')}
                onChange={(e) =>
                  methods.setValue('waferSize', e.target.value as dt.WAFER_SIZE, {
                    shouldValidate: true,
                  })
                }
              >
                {WAFER_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!canEdit || submitting || lockGeometry}>
              <InputLabel>ID side</InputLabel>
              <Select
                label="ID side"
                value={methods.watch('idSide')}
                onChange={(e) =>
                  methods.setValue('idSide', e.target.value as 'top' | 'bottom', {
                    shouldValidate: true,
                  })
                }
              >
                {ID_SIDE_OPTIONS.map((side) => (
                  <MenuItem key={side} value={side}>
                    {side}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AppTextField
              label="Mark offset (degrees)"
              type="number"
              fullWidth
              size="small"
              disabled={!canEdit || submitting}
              inputProps={{ step: 0.1 }}
              helperText="CCW degrees from notch/flat to center of ID mark"
              {...methods.register('markOffsetDeg', { valueAsNumber: true })}
              error={Boolean(methods.formState.errors.markOffsetDeg)}
            />
            <LightingModesEditor
              value={modes}
              disabled={!canEdit || submitting}
              onChange={(next) =>
                methods.setValue('startingLightingModes', next, { shouldValidate: true })
              }
            />
            {methods.formState.errors.startingLightingModes && (
              <Alert severity="error">
                {methods.formState.errors.startingLightingModes.message ??
                  'Invalid lighting modes'}
              </Alert>
            )}
          </Stack>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <AppButton onClick={onClose} disabled={submitting}>
          Cancel
        </AppButton>
        {canEdit && (
          <AppButton
            variant="contained"
            disabled={submitting}
            onClick={methods.handleSubmit(handleSubmit)}
          >
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create'}
          </AppButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
